import { useState, useMemo, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Plus, RefreshCw } from 'lucide-react'
import { CuentasTable } from '@/components/cuentas/CuentasTable'
import { CuentaFormDialog } from '@/components/cuentas/CuentaFormDialog'
import { CuentaDetalleDialog } from '@/components/cuentas/CuentaDetalleDialog'
import {
  CuentasFiltros,
  type CuentasFiltrosState,
} from '@/components/cuentas/CuentasFiltros'
import { FILTROS_CUENTAS_INICIALES } from '@/components/cuentas/cuentasFiltros.constants'
import type { CuentaFormValues } from '@/components/cuentas/CuentaFormNew'
import { MONEDA_LABELS, MONEDAS, type Moneda } from '@/components/cuentas/forms/cuentaSchema'

// Helper para formatear moneda de forma segura
function formatCurrency(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency }).format(value)
  } catch {
    // Fallback si la moneda no es válida
    return `${currency} ${value.toFixed(2)}`
  }
}
import {
  useCuentasConSaldo,
  useCrearCuenta,
  useActualizarCuenta,
  useEliminarCuenta,
  useToggleCuentaActiva,
} from '@/hooks/useCuentas'
import type { CuentaConSaldo } from '@/services/cuentasService'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'

// Helper para parsear query params a estado de filtros
function parseSearchParamsToFiltros(searchParams: URLSearchParams): CuentasFiltrosState {
  return {
    busqueda: searchParams.get('q') || '',
    tipo: searchParams.get('tipo') || 'todos',
    institucion: searchParams.get('institucion') || 'todos',
    moneda: searchParams.get('moneda') || 'todos',
    mostrarInactivas: searchParams.get('inactivas') === 'true',
  }
}

// Helper para convertir filtros a query params
function filtrosToSearchParams(filtros: CuentasFiltrosState): URLSearchParams {
  const params = new URLSearchParams()

  if (filtros.busqueda) params.set('q', filtros.busqueda)
  if (filtros.tipo !== 'todos') params.set('tipo', filtros.tipo)
  if (filtros.institucion !== 'todos') params.set('institucion', filtros.institucion)
  if (filtros.moneda !== 'todos') params.set('moneda', filtros.moneda)
  if (filtros.mostrarInactivas) params.set('inactivas', 'true')

  return params
}

export function Cuentas() {
  // URL search params para persistencia
  const [searchParams, setSearchParams] = useSearchParams()

  // Estado de filtros inicializado desde URL
  const [filtros, setFiltros] = useState<CuentasFiltrosState>(() =>
    parseSearchParamsToFiltros(searchParams)
  )

  // Estado de dialogs
  const [dialogOpen, setDialogOpen] = useState(false)
  const [detalleOpen, setDetalleOpen] = useState(false)
  const [cuentaEditar, setCuentaEditar] = useState<CuentaConSaldo | null>(null)
  const [cuentaDetalle, setCuentaDetalle] = useState<CuentaConSaldo | null>(null)
  const [cuentaEliminar, setCuentaEliminar] = useState<CuentaConSaldo | null>(null)

  // Queries y mutations
  // SIEMPRE traer todas las cuentas para poder contar inactivas y filtrar localmente
  const { data: todasLasCuentas = [], isLoading, error, refetch } = useCuentasConSaldo(true)
  const crearCuenta = useCrearCuenta()
  const actualizarCuenta = useActualizarCuenta()
  const eliminarCuenta = useEliminarCuenta()
  const toggleActiva = useToggleCuentaActiva()

  // Sincronizar filtros con URL
  useEffect(() => {
    const newParams = filtrosToSearchParams(filtros)
    const currentParams = new URLSearchParams(searchParams)

    // Solo actualizar si hay cambios
    if (newParams.toString() !== currentParams.toString()) {
      setSearchParams(newParams, { replace: true })
    }
  }, [filtros, searchParams, setSearchParams])

  // Estadísticas globales (siempre basadas en todas las cuentas)
  const stats = useMemo(() => {
    const activas = todasLasCuentas.filter((c) => c.activa).length
    const inactivas = todasLasCuentas.filter((c) => !c.activa).length
    return { activas, inactivas, total: todasLasCuentas.length }
  }, [todasLasCuentas])

  // Extraer instituciones únicas para el filtro (de todas las cuentas)
  const institucionesDisponibles = useMemo(() => {
    const instituciones = new Set<string>()
    todasLasCuentas.forEach((cuenta) => {
      if (cuenta.institucion) {
        instituciones.add(cuenta.institucion)
      }
    })
    return Array.from(instituciones).sort()
  }, [todasLasCuentas])

  // Filtrado local (incluye filtro de activas/inactivas)
  const cuentasFiltradas = useMemo(() => {
    return todasLasCuentas.filter((cuenta) => {
      // Filtro por estado activo/inactivo
      // Si mostrarInactivas es false, solo mostrar activas
      if (!filtros.mostrarInactivas && !cuenta.activa) {
        return false
      }
      // Filtro por búsqueda
      if (filtros.busqueda) {
        const termino = filtros.busqueda.toLowerCase()
        const coincide =
          cuenta.nombre?.toLowerCase().includes(termino) ||
          cuenta.institucion?.toLowerCase().includes(termino) ||
          cuenta.cbu_cvu_alias?.toLowerCase().includes(termino)
        if (!coincide) return false
      }
      // Filtro por tipo
      if (filtros.tipo !== 'todos' && cuenta.tipo !== filtros.tipo) {
        return false
      }
      // Filtro por institución
      if (filtros.institucion !== 'todos' && cuenta.institucion !== filtros.institucion) {
        return false
      }
      // Filtro por moneda
      if (filtros.moneda !== 'todos' && cuenta.moneda !== filtros.moneda) {
        return false
      }
      return true
    })
  }, [todasLasCuentas, filtros])

  // Saldos agrupados por moneda de las cuentas filtradas
  const saldosPorMoneda = useMemo(() => {
    const saldos: Record<string, number> = {}
    cuentasFiltradas.forEach((c) => {
      const moneda = c.moneda || 'ARS'
      saldos[moneda] = (saldos[moneda] || 0) + (c.saldo_actual || 0)
    })
    return saldos
  }, [cuentasFiltradas])

  // Monedas que tienen saldo (filtradas a monedas conocidas)
  const monedasConSaldo = useMemo(() => {
    return Object.keys(saldosPorMoneda)
      .filter((m): m is Moneda => MONEDAS.includes(m as Moneda))
      .sort()
  }, [saldosPorMoneda])

  // Handlers
  const handleNuevaCuenta = useCallback(() => {
    setCuentaEditar(null)
    setDialogOpen(true)
  }, [])

  const handleVerDetalle = useCallback((cuenta: CuentaConSaldo) => {
    setCuentaDetalle(cuenta)
    setDetalleOpen(true)
  }, [])

  const handleEditarCuenta = useCallback((cuenta: CuentaConSaldo) => {
    setCuentaEditar(cuenta)
    setDialogOpen(true)
  }, [])

  const handleEliminarCuenta = useCallback((cuenta: CuentaConSaldo) => {
    setCuentaEliminar(cuenta)
  }, [])

  const handleToggleActiva = useCallback(async (cuenta: CuentaConSaldo) => {
    try {
      await toggleActiva.mutateAsync({ id: cuenta.id, activa: !cuenta.activa })
      toast.success(cuenta.activa ? 'Cuenta desactivada' : 'Cuenta activada')
    } catch (err) {
      toast.error('Error al cambiar estado de la cuenta')
      console.error(err)
    }
  }, [toggleActiva])

  const handleSubmit = useCallback(async (data: CuentaFormValues) => {
    try {
      const cuentaData = {
        nombre: data.nombre,
        tipo: data.tipo,
        institucion: data.institucion || null,
        cbu_cvu_alias: data.cbu_cvu_alias || null,
        moneda: data.moneda || 'ARS',
        observaciones: data.observaciones || null,
        activa: data.activa,
        saldo_inicial: data.saldo_inicial ?? 0,
      }

      if (cuentaEditar) {
        await actualizarCuenta.mutateAsync({
          id: cuentaEditar.id,
          data: cuentaData,
        })
        toast.success('Cuenta actualizada correctamente')
      } else {
        await crearCuenta.mutateAsync(cuentaData)
        toast.success('Cuenta creada correctamente')
      }
      setDialogOpen(false)
      setCuentaEditar(null)
    } catch (err) {
      toast.error(
        cuentaEditar
          ? 'Error al actualizar la cuenta'
          : 'Error al crear la cuenta'
      )
      console.error(err)
    }
  }, [cuentaEditar, actualizarCuenta, crearCuenta])

  const handleConfirmarEliminar = useCallback(async () => {
    if (!cuentaEliminar) return
    try {
      await eliminarCuenta.mutateAsync(cuentaEliminar.id)
      toast.success('Cuenta eliminada correctamente')
      setCuentaEliminar(null)
    } catch (err) {
      toast.error('Error al eliminar la cuenta. Puede tener movimientos asociados.')
      console.error(err)
    }
  }, [cuentaEliminar, eliminarCuenta])

  const handleFiltrosChange = useCallback((nuevosFiltros: CuentasFiltrosState) => {
    setFiltros(nuevosFiltros)
  }, [])

  const handleLimpiarFiltros = useCallback(() => {
    setFiltros(FILTROS_CUENTAS_INICIALES)
  }, [])

  // Handler para batch toggle activa
  const handleBatchToggleActiva = useCallback(async (cuentasBatch: CuentaConSaldo[], activa: boolean) => {
    const promises = cuentasBatch.map((cuenta) =>
      toggleActiva.mutateAsync({ id: cuenta.id, activa })
    )
    await Promise.all(promises)
  }, [toggleActiva])

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Cuentas"
        description="Gestiona las cuentas bancarias y medios de pago"
      />

      {/* Contenedor principal con min-h-0 para scroll correcto */}
      <div className="flex-1 flex flex-col p-6 gap-6 min-h-0">
        {/* Barra de filtros y acciones - no se encoge */}
        <div className="shrink-0 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <CuentasFiltros
            filtros={filtros}
            onChange={handleFiltrosChange}
            institucionesDisponibles={institucionesDisponibles}
            totalInactivas={stats.inactivas}
            className="flex-1"
          />

          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetch()}
              aria-label="Refrescar cuentas"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button onClick={handleNuevaCuenta}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva Cuenta
            </Button>
          </div>
        </div>

        {/* Resumen - no se encoge */}
        <div className="shrink-0 flex flex-wrap gap-4">
          <div className="rounded-lg border bg-card p-4 min-w-[140px]">
            <p className="text-sm text-muted-foreground">Total Cuentas</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="rounded-lg border bg-card p-4 min-w-[140px]">
            <p className="text-sm text-muted-foreground">Activas</p>
            <p className="text-2xl font-bold text-green-600">{stats.activas}</p>
          </div>
          <div className="rounded-lg border bg-card p-4 min-w-[140px]">
            <p className="text-sm text-muted-foreground">Inactivas</p>
            <p className="text-2xl font-bold text-muted-foreground">{stats.inactivas}</p>
          </div>
          {/* Saldos por moneda - híbrido según filtro */}
          {filtros.moneda !== 'todos' ? (
            // Moneda específica filtrada: mostrar solo ese saldo
            <div className="rounded-lg border bg-card p-4 min-w-[160px]">
              <p className="text-sm text-muted-foreground">
                Saldo {MONEDA_LABELS[filtros.moneda as Moneda] || filtros.moneda}
              </p>
              <p className={`text-2xl font-bold tabular-nums ${(saldosPorMoneda[filtros.moneda] || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(saldosPorMoneda[filtros.moneda] || 0, filtros.moneda)}
              </p>
            </div>
          ) : monedasConSaldo.length > 0 ? (
            // Sin filtro de moneda: mostrar saldo por cada moneda presente
            monedasConSaldo.map((moneda) => (
              <div key={moneda} className="rounded-lg border bg-card p-4 min-w-[160px]">
                <p className="text-sm text-muted-foreground">
                  Saldo {MONEDA_LABELS[moneda]}
                </p>
                <p className={`text-2xl font-bold tabular-nums ${saldosPorMoneda[moneda] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(saldosPorMoneda[moneda], moneda)}
                </p>
              </div>
            ))
          ) : (
            // Sin cuentas: mostrar ARS con $0
            <div className="rounded-lg border bg-card p-4 min-w-[160px]">
              <p className="text-sm text-muted-foreground">Saldo {MONEDA_LABELS.ARS}</p>
              <p className="text-2xl font-bold tabular-nums text-muted-foreground">
                {formatCurrency(0, 'ARS')}
              </p>
            </div>
          )}
        </div>

        {/* Contenedor de la tabla - ocupa espacio restante con scroll interno */}
        <div className="flex-1 min-h-0 flex flex-col">
          {error ? (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <p className="text-destructive">
                Error al cargar las cuentas. Verifica la conexión con Supabase.
              </p>
            </div>
          ) : (
            <CuentasTable
              cuentas={cuentasFiltradas}
              isLoading={isLoading}
              onEditar={handleEditarCuenta}
              onEliminar={handleEliminarCuenta}
              onToggleActiva={handleToggleActiva}
              onVerDetalle={handleVerDetalle}
              onBatchToggleActiva={handleBatchToggleActiva}
            />
          )}

          {/* Resumen de filtrado */}
          {!isLoading && !error && cuentasFiltradas.length !== todasLasCuentas.length && (
            <div className="shrink-0 flex items-center justify-between text-sm text-muted-foreground pt-2">
              <span>
                Mostrando {cuentasFiltradas.length} de {todasLasCuentas.length} cuentas
              </span>
              <Button variant="link" size="sm" onClick={handleLimpiarFiltros}>
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Dialog para ver detalle */}
      <CuentaDetalleDialog
        cuenta={cuentaDetalle}
        open={detalleOpen}
        onOpenChange={setDetalleOpen}
        onEditar={handleEditarCuenta}
        onToggleActiva={handleToggleActiva}
      />

      {/* Dialog para crear/editar cuenta */}
      <CuentaFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        cuenta={cuentaEditar}
        onSubmit={handleSubmit}
        isLoading={crearCuenta.isPending || actualizarCuenta.isPending}
        instituciones={institucionesDisponibles}
      />

      {/* Dialog de confirmación para eliminar */}
      <AlertDialog
        open={!!cuentaEliminar}
        onOpenChange={(open) => !open && setCuentaEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar cuenta?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la
              cuenta <strong>{cuentaEliminar?.nombre}</strong>.
              <br /><br />
              <span className="text-destructive">
                Nota: Si la cuenta tiene movimientos asociados, no podrá ser eliminada.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarEliminar}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminarCuenta.isPending ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
